import { Dialog, notifyError, notifySuccess } from '@/components'
import { useUpdateTrafficDetectorMutation } from '@/modules/add-new-detector/services/updateTrafficDetectorService'
import MapNew from '@/components/Organisms/Map/Map'
import { UnitSearchReq } from '@/modules/devices-list/types/newUnitsType'
import { useGetUnitsHomeMapMutation } from '@/modules/maps/services/homeMapService'
import { timeout } from '@/utils'
import cn from '@/utils/cn'
import { useChangeSet } from '@/utils/useChangeSet'
import { yupResolver } from '@hookform/resolvers/yup'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import Input from '@/components/Molecules/Input/Input'
import UnitSelect from '@/components/Molecules/Select/Select'
import MultipleSelect from '@/components/Molecules/MultipleSelect/MultipleSelect'
import FormAddHeader from '@/components/Molecules/FormAddHeader/FormAddHeader'
import { sxWrapper } from '@/pages/Components/AddPagesWrapper'
import SystemUploadImg from '@/components/Organisms/SystemUploadImg/SystemUploadImg'
const s = cn('add-new-dit')

interface IUnitData {
  setLoader: Dispatch<SetStateAction<boolean>>
}

export const DIRECTION_ITEMS = Array.from({ length: 8 }, (_, i) => ({
  id: i + 1,
  name: String(i + 1),
}))

const center = [47.2313, 39.7233]
const pointsIfEmpty = [
  [48.024402067130715, 39.85466330972504],
  [46.780699672601415, 39.807971415195674],
]

export interface IUnitsFields {
  name: string
  ip: string
  port: string
  address: string
  oncomingDirection: number[]
  oppositeDirection: number[]
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Название не может быть пустым')
    .trim('Название не может быть пустым')
    .max(30, 'Поле должно содержать не более 30 символов'),
  ip: Yup.string()
    .required('IP Адрес не может быть пустым')
    .trim('IP Адрес не может быть пустым')
    .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
      message: 'Недопустимые символы',
      excludeEmptyString: true,
    })
    .test('ip', 'Адрес имеет некорректный формат', (value) => {
      return value === undefined || value.trim() === ''
        ? true
        : value.split('.').find((i) => parseInt(i, 10) > 255) === undefined
    })
    .max(15, 'IP Адрес должен содержать не более 15 символов'),

  oncomingDirection: Yup.array()
    .min(1, 'Укажите попутное направление')
    .required('Укажите попутное направление'),
  oppositeDirection: Yup.array()
    .min(1, 'Укажите встречное направление')
    .required('Укажите встречное направление'),
  port: Yup.number()
    .required('Порт не может быть пустым')
    .typeError('Недопустимые символы')
    .moreThan(1, 'Порт должен содержать не менее 5 символов')
    .lessThan(99999, 'Порт должен содержать не более 5 символов'),
  address: Yup.string()
    .required('Адрес не может быть пустым')
    .trim('Адрес не может быть пустым')
    .max(250, 'Поле должно содержать не более 250 символов'),
})
const defaultFormValues: IUnitsFields = {
  name: '',
  address: '',
  oncomingDirection: [],
  oppositeDirection: [],
  port: '',
  ip: '',
}

const Form: FC<IUnitData> = ({ setLoader }) => {
  const navigate = useNavigate()
  const [updateTrafficDetector] = useUpdateTrafficDetectorMutation()
  const [getDataUnits, { data: locations }] = useGetUnitsHomeMapMutation()
  const [points, setPoints] = useState<number[][]>([])

  const onGetUnits = async () => {
    try {
      const usersSearchData: Omit<UnitSearchReq, 'page'> = {
        value: '',
        deviceTypeFilter: [0],
        deviceStateFilter: [0],
        favoriteId: 0,
        lightingTypeId: 0,
      }
      const res = await getDataUnits(usersSearchData).unwrap()
      if (res.success) {
        const units =
          res.data.data.length === 0
            ? pointsIfEmpty
            : res.data.data.map((unit) => unit.coordinates)
        setPoints(() => units)
      }
    } catch (e) {
      console.error(e)
    }
  }
  useEffect(() => {
    onGetUnits().then()
    reset(defaultFormValues)
  }, [])

  const [openDialogSave, setOpenDialogSave] = useState(false)
  const [imageId, setImageId] = useState<string | null>(null)
  const [newCoords, setNewCoords] = useState<number[]>([
    47.06587193746529, 39.435380396518724,
  ])

  const refMark = useRef<any>(null)

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState,
    watch,
    getValues,
    setValue,
  } = useForm<IUnitsFields>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(validationSchema),
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  })

  const onSaveBtnClick = () => {
    trigger().then(() => {
      if (
        !getValues('name') ||
        !getValues('address') ||
        !getValues('ip') ||
        !getValues('port') ||
        !getValues('oncomingDirection').length ||
        !getValues('oppositeDirection').length
      ) {
        return
      }
      if (
        !formState.errors.name &&
        !formState.errors.oncomingDirection &&
        !formState.errors.oppositeDirection &&
        !formState.errors.ip &&
        !formState.errors.port &&
        !formState.errors.address
      ) {
        setOpenDialogSave(true)
      }
    })
  }

  const onSubmit = async (data: IUnitsFields) => {
    try {
      setOpenDialogSave(false)
      setLoader(true)
      const payload = {
        imageId: imageId ?? '',
        id: 0,
        name: data.name,
        address: data.address,
        coordinates: JSON.stringify(newCoords),
        ip: data.ip,
        port: Number(data.port),
        sameDirectionSensors: data.oncomingDirection,
        oppositeDirectionSensors: data.oppositeDirection,
      }
      const res = await updateTrafficDetector(payload).unwrap()
      if (res.success) {
        notifySuccess('Новое устройство успешно добавлено!', 1000)
        await timeout(1000)
        navigate('/new-units')
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoader(false)
    }
  }

  // WTF ???
  useChangeSet(() => {
    if (watch('name').length) {
      trigger('name')
    }
    if (watch('address').length) {
      trigger('address')
    }
  }, [watch('name'), watch('address')])

  useChangeSet(() => {
    setValue('name', `${watch('name')} `, { shouldDirty: true })
  }, [newCoords])
  const handleChangeCoords = (coords: number[]) => {
    setNewCoords(coords)
  }

  return (
    <>
      <Dialog
        message='Новая система будет сохранена, продолжить?'
        open={openDialogSave}
        handleClose={() => setOpenDialogSave(false)}
        handleAgree={() => {
          if (Object.keys(formState.errors).length >= 1) {
            notifyError('Исправте ошибки в форме')
          }
          handleSubmit(onSubmit)()
        }}
      />
      <Box sx={sxWrapper}>
        <FormAddHeader title={'Добавление Детектора'} />
        <Box className={'wrapper'}>
          <Box
            className={'left'}
            style={{ width: '100%' }}
          >
            <Box className={'row'}>
              <Input
                name='name'
                type='text'
                label='Название Детектора'
                control={control}
              />
              <Input
                name='ip'
                type='text'
                label='IP адрес'
                control={control}
              />
              <Input
                name='port'
                type='text'
                label='Порт'
                control={control}
              />
            </Box>
            <Box className={'row'}>
              <Input
                multiline
                minRows={1}
                maxRows={5}
                name='address'
                type='text'
                label='Адрес установки'
                control={control}
              />
              <MultipleSelect
                label={'Попутное направление'}
                control={control as any}
                name='oncomingDirection'
                items={DIRECTION_ITEMS}
                disabledValues={watch('oppositeDirection')}
              />
              <MultipleSelect
                label={'Встречное направление'}
                control={control as any}
                name='oppositeDirection'
                items={DIRECTION_ITEMS}
                disabledValues={watch('oncomingDirection')}
              />
            </Box>
          </Box>
        </Box>

        <Box className={'mapContainer'}>
          <MapNew
            center={center}
            dataUnits={locations}
            setNewCoords={handleChangeCoords}
            newCoords={newCoords}
            draggable={true}
          />
        </Box>
        <Box className={'footerButtons'}>
          <SystemUploadImg
            imageId={imageId}
            onImageUploaded={setImageId}
          />
          <LoadingButton
            loading={false}
            variant='outlined'
            onClick={onSaveBtnClick}
            disabled={!formState.isDirty}
          >
            <span>Сохранить</span>
          </LoadingButton>
        </Box>
      </Box>
    </>
  )
}

export default Form
